import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, X, Loader2, Eye } from "lucide-react";
import { api, formatApiError, photoUrl, CATEGORIES, LANGUAGES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Field = ({ label, children }) => (
  <label className="block"><span className="eyebrow !text-neutral-500">{label}</span>{children}</label>
);

const Toggle = ({ options, value, onChange, prefix }) => (
  <div className="flex flex-wrap gap-2 mt-3">
    {options.map((o) => {
      const on = value.includes(o);
      return (
        <button type="button" key={o} onClick={() => onChange(on ? value.filter((x) => x !== o) : [...value, o])} data-testid={`${prefix}-${o}`}
          className={`px-3 py-1.5 text-xs border transition-colors ${on ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5" : "border-white/10 text-neutral-400 hover:border-white/30"}`}>{o}</button>
      );
    })}
  </div>
);

export default function ProfileEditor() {
  const { user, setUser } = useAuth();
  const p = user.profile || {};
  const [form, setForm] = useState({
    name: user.name, city: p.city || "", bio: p.bio || "", age: p.age || "", height: p.height || "", size: p.size || "",
    shoes: p.shoes || "", eyes: p.eyes || "", hair: p.hair || "", languages: p.languages || [], categories: p.categories || [], rate: p.rate || "", telegram: p.telegram || "", phone: p.phone || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const num = (v) => (v === "" || v == null ? null : Number(v));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.put("/profile", { ...form, age: num(form.age), height: num(form.height), rate: num(form.rate) });
      setUser(r.data);
      toast.success("Profilo salvato");
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/profile/photos", fd);
        setUser(r.data);
      }
      toast.success("Foto caricate");
    } catch (err) { toast.error(formatApiError(err)); } finally { setUploading(false); e.target.value = ""; }
  };

  const remove = async (path) => {
    const r = await api.post("/profile/photos/delete", { path });
    setUser(r.data);
  };

  const photos = user.profile?.photos || [];

  return (
    <main className="max-w-6xl mx-auto px-6 lg:px-10 pt-16" data-testid="profile-editor-page">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="eyebrow">Il tuo book</p>
          <h1 className="font-serif text-4xl sm:text-5xl mt-3">Profilo professionale</h1>
        </div>
        <Link to={`/hostess/${user.id}`} className="btn-ghost !py-2.5" data-testid="profile-preview-link"><Eye className="w-4 h-4" /> Anteprima pubblica</Link>
      </div>
      {user.hidden && (
        <p className="mt-8 text-sm text-amber-400 border border-amber-400/30 bg-amber-400/5 px-5 py-4" data-testid="profile-hidden-banner">
          Il tuo profilo è stato nascosto dall'amministrazione perché non rispetta le regole: non compare nelle ricerche e non puoi candidarti agli eventi. Correggi i contenuti non consentiti (ad es. contatti in bio o nelle foto) e attendi la revisione.
        </p>
      )}

      <section className="mt-14">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-2xl">Galleria foto</h2>
          <span className="text-xs font-mono text-neutral-500">{photos.length} foto · la prima è la copertina</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-6" data-testid="profile-gallery">
          {photos.map((ph, i) => (
            <div key={ph} className="relative aspect-[3/4] group overflow-hidden border border-white/5" data-testid={`profile-photo-${i}`}>
              <img src={photoUrl(ph)} alt="" className="w-full h-full object-cover" />
              {i === 0 && <span className="absolute top-2 left-2 eyebrow bg-[#0B0C0E]/80 px-2 py-0.5 !text-[0.6rem]">Copertina</span>}
              <button onClick={() => remove(ph)} className="absolute top-2 right-2 w-7 h-7 bg-[#0B0C0E]/80 flex items-center justify-center opacity-0 group-hover:opacity-100 hover:text-red-400 transition-opacity" data-testid={`profile-photo-delete-${i}`} aria-label="Elimina"><X className="w-4 h-4" /></button>
            </div>
          ))}
          <button type="button" onClick={() => fileRef.current.click()} disabled={uploading}
            className="aspect-[3/4] border border-dashed border-white/15 hover:border-[#D4AF37] text-neutral-500 hover:text-[#D4AF37] flex flex-col items-center justify-center gap-2 transition-colors" data-testid="profile-photo-upload-button">
            {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Plus className="w-6 h-6" strokeWidth={1.3} />}
            <span className="text-xs uppercase tracking-[0.15em]">{uploading ? "Caricamento" : "Aggiungi foto"}</span>
          </button>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={upload} data-testid="profile-photo-input" />
        </div>
      </section>

      <form onSubmit={save} className="mt-16 grid lg:grid-cols-2 gap-x-14 gap-y-8" data-testid="profile-form">
        <Field label="Username"><input className="lux-input" value={form.name} onChange={set("name")} data-testid="profile-name-input" /></Field>
        <Field label="Città"><input className="lux-input" value={form.city} onChange={set("city")} placeholder="Es. Milano" data-testid="profile-city-input" /></Field>
        <div className="lg:col-span-2"><Field label="Biografia"><textarea rows={3} className="lux-input resize-none" value={form.bio} onChange={set("bio")} placeholder="Esperienze, punti di forza, eventi a cui hai partecipato…" data-testid="profile-bio-input" /></Field>
          <p className="text-xs text-neutral-400 mt-2" data-testid="profile-bio-hint">Non inserire contatti (telefono, email, link o social) nella biografia: i clienti li vedranno solo dopo lo sblocco.</p></div>
        <div className="grid grid-cols-2 gap-6">
          <Field label="Età"><input type="number" className="lux-input" value={form.age} onChange={set("age")} data-testid="profile-age-input" /></Field>
          <Field label="Altezza cm"><input type="number" className="lux-input" value={form.height} onChange={set("height")} data-testid="profile-height-input" /></Field>
        </div>
        <div className="grid grid-cols-4 gap-6">
          <Field label="Taglia"><input className="lux-input" value={form.size} onChange={set("size")} data-testid="profile-size-input" /></Field>
          <Field label="Scarpe"><input className="lux-input" value={form.shoes} onChange={set("shoes")} data-testid="profile-shoes-input" /></Field>
          <Field label="Occhi"><input className="lux-input" value={form.eyes} onChange={set("eyes")} data-testid="profile-eyes-input" /></Field>
          <Field label="Capelli"><input className="lux-input" value={form.hair} onChange={set("hair")} data-testid="profile-hair-input" /></Field>
        </div>
        <Field label="Categorie"><Toggle options={CATEGORIES} value={form.categories} onChange={(v) => setForm({ ...form, categories: v })} prefix="profile-category" /></Field>
        <Field label="Lingue"><Toggle options={LANGUAGES} value={form.languages} onChange={(v) => setForm({ ...form, languages: v })} prefix="profile-language" /></Field>
        <Field label="Username Telegram * (visibile solo ai clienti che sbloccano i contatti)">
          <div className="flex items-center gap-1"><span className="text-neutral-500">@</span><input className="lux-input" value={form.telegram} onChange={set("telegram")} placeholder="tuo_username" required minLength={5} data-testid="profile-telegram-input" /></div>
        </Field>
        <Field label="Numero di telefono (opzionale, visibile solo dopo lo sblocco)">
          <input type="tel" className="lux-input" value={form.phone} onChange={set("phone")} placeholder="+39 333 123 4567" data-testid="profile-phone-input" />
        </Field>
        <div className="lg:col-span-2"><button disabled={saving} className="btn-gold" data-testid="profile-save-button">{saving ? "Salvataggio…" : "Salva profilo"}</button></div>
      </form>
    </main>
  );
}
